import { toQueryString } from 'types/utils'

export const PREFIXES = {
  GAME: 'games',
  PARTNER_PAGES: 'partner-pages',
  USERS: 'users',
  B2B: 'b2b',
  WALLET: 'wallet',
}
export const GameStatus = {
  ACTIVE: 'ACTIVE',
  UPCOMING: 'UPCOMING',
  FINISHED: 'FINISHED',
} as const

export type GameStatusType = (typeof GameStatus)[keyof typeof GameStatus]

export const API_ENDPOINTS = {
  GET_USER_SELECTIONS: (gameId: number, userId: number) =>
    `/${PREFIXES.GAME}/v2/selections/${gameId}/users/${userId}`,
  GET_SELECTIONS_BY_MATCH: (gameId: number, matchId: number | undefined) =>
    `/${PREFIXES.GAME}/v2/selections/${gameId}/matches/${matchId}`,
  GET_MY_SELECTIONS: (gameId: number) => `/${PREFIXES.GAME}/v2/selections/${gameId}/users/me`,
  GET_GAME_SELECTIONS: (gameId: number) => `/${PREFIXES.GAME}/v2/selections/${gameId}`,
  SET_GAME_TIEBREAKER: (gameId: number) => `/b2b/${PREFIXES.GAME}/${gameId}/tiebreaker`,
  JOIN_GAME: (gameId: number) => `/${PREFIXES.GAME}/v2/join/${gameId}`,
  GET_GAME_RESULTS: (gameId: number) => `/${PREFIXES.GAME}/${gameId}/results`,
  GET_GAME_INFO: (gameId: number) => `/${PREFIXES.GAME}/${gameId}`,
  GET_GAME_INFO_V2: (gameId: number) => `/${PREFIXES.GAME}/v2/${gameId}`,
  GET_SCORE_WORDING: `${PREFIXES.B2B}/wording/ptw`,
  GET_POP_UP_DATA: `/api/v2/hints`,
  GET_PAGE_STYLES: (subdomain: string) => `/b2b/${PREFIXES.PARTNER_PAGES}/${subdomain}`,
  GET_GAME_STATS: `/${PREFIXES.GAME}/statistics/`,
  GET_MATCH_STATS: (matchId: number) => `/${PREFIXES.GAME}/v2/matches/${matchId}/stats`,
  CREATE_MINI_LEAGUE: (gameId: number) => `/${PREFIXES.GAME}/v2/b2b/create/${gameId}/mini-league`,
  EDIT_MINI_LEAGUE: (miniLeagueId: number) =>
    `/${PREFIXES.GAME}/v2/b2b/edit/mini-league/${miniLeagueId}`,
  JOIN_TO_MINI_LEAGUE: (miniLeagueId: number) =>
    `/${PREFIXES.GAME}/v2/b2b/join/${miniLeagueId}/mini-league`,
  REMOVE_USER_FROM_MINI_LEAGUE: (miniLeagueId: number) =>
    `/${PREFIXES.GAME}/v2/b2b/remove-participant/${miniLeagueId}/mini-league`,
  REMOVE_MINI_LEAGUE: (miniLeagueId: number) =>
    `/${PREFIXES.GAME}/v2/b2b/cancel/${miniLeagueId}/mini-league`,
  UPDATE_USER_PROFILE: `/${PREFIXES.USERS}/me`,
  GET_USER_PROFILE: `/${PREFIXES.USERS}/me`,
  GET_USER_EMAIL: `/${PREFIXES.USERS}/email/check`,
  GET_SITE_CONTENT: `/b2b/site-content`,
  GET_MY_LEAGUES: (gameId: number) => `/${PREFIXES.GAME}/v2/b2b/${gameId}/mini-leagues`,
  GET_ALL_MINI_LEAGUES: (gameId: number) => `/${PREFIXES.GAME}/v2/b2b/search/mini-league/${gameId}`,
  GET_IF_DATA_COLLECTED: `/${PREFIXES.GAME}/v2/b2b/collected-data/`,
  GET_GAME_LEADERBOARD: (gameId: number) => `/${PREFIXES.GAME}/leaderboard/${gameId}`,
  GET_GAME_LEADERBOARD_WITH_CELEBRITIES: (gameId: number) =>
    `/${PREFIXES.GAME}/leaderboard/${gameId}/celebrities`,
  SET_IF_DATA_COLLECTED: (gameId: number) => `/${PREFIXES.GAME}/v2/b2b/collected-data/${gameId}`,
  GET_POSITION_DATA: (gameId: number) => `/${PREFIXES.GAME}/v2/b2b/position/${gameId}`,
  REGISTER: `/${PREFIXES.USERS}/sign-up`,
  LOGIN: `/${PREFIXES.USERS}/sign-in`,
  GET_WORKFLOW: `/${PREFIXES.B2B}/forms/workflow`,
  RESET_PASSWORD: `/${PREFIXES.USERS}/password/reset`,
  SET_PASSWORD: `/${PREFIXES.USERS}/password/set`,
  SEND_ANSWERS: `/${PREFIXES.B2B}/forms/answer`,
  SEND_EMAIL_VERIFICATION_OTP: `/${PREFIXES.USERS}/otp/send`,
  VERIFY_EMAIL_OTP: `/${PREFIXES.USERS}/otp/verify`,
  IS_USER_JOINED: (gameId: number) => `/${PREFIXES.GAME}/v2/${gameId}/is-joined`,
  GET_ROUND_BY_ID: (roundId: number) => `/${PREFIXES.GAME}/v2/rounds/${roundId}`,
  GET_ROUNDS_BY_GAME: (gameId: number) => `/${PREFIXES.GAME}/v2/rounds/game/${gameId}`,
  GET_ADDRESS: `/${PREFIXES.USERS}/verification/lookup`,
  GET_GOLF_DATA: `/${PREFIXES.GAME}/v2/live-scoring/golf/player-data`,
  USER_EMAIL_PREFERENCES: `/${PREFIXES.USERS}/user-preferences`,
  GET_SPEND_LIMIT: `/${PREFIXES.USERS}/me/spend-limit`,
  SET_SPEND_LIMIT: `/${PREFIXES.USERS}/me/spend-limit`,
  POST_WALLET_WITHDRAWAL: `/${PREFIXES.GAME}/wallet/withdrawal`,
  GET_ADDITIONAL_QUESTIONS: `/${PREFIXES.B2B}/forms/additional-questions`,
  EXTERNAL_LOGIN: `${PREFIXES.USERS}/external/login`,
  GET_PARTNER_GAMES: (
    partnerId: number,
    params?: {
      status?: GameStatusType
      sport_name?: string
      tournament_name?: string
    },
  ) => {
    const query = toQueryString(params ?? {})
    return `/${PREFIXES.GAME}/v2/partners/${partnerId}/games${query ? `?${query}` : ''}`
  },
  POST_CHARITY_PAGE_CONSENT: (gameId: number) =>
    `/${PREFIXES.B2B}/${PREFIXES.GAME}/${gameId}/charity-page/consent`,
}

export const API_ERRORS = {
  VERIFY_EMAIL: 'Please verify your email address in order to join the game',
  USER_HAS_NOT_JOINED_PARENT_GAME:
    'Please make your predictions and enter the game before creating or joining a private league.',
  USER_ALREADY_JOINED: 'You have already joined this mini-league',
  GAME_FINISHED: 'Main game has already finished',
  OTP_ALREADY_EXISTS: 'One-time-password has been already sent',
  USER_ALREADY_VERIFIED: 'Your email is already verified',
  OTP_EXPIRED_OR_DOES_NOT_EXIST: 'One-time-password has expired or does not exist',
  OTP_INVALID: 'Incorrect one-time-password',
  USER_IS_NOT_CREATOR: 'You are not the creator of this private league',
  EXTERNAL_PROVIDER_USER_NOT_FOUND: 'User not found in external provider',
  EXTERNAL_PROVIDER_NOT_CONFIGURED: 'External login provider not configured',
  EXTERNAL_PROVIDER_TIMEOUT_ERROR: 'External provider system timeout. Please try again.',
  EXTERNAL_PROVIDER_FAILED_TO_FETCH_USER_DATA: 'Failed to fetch user data from external provider',
  EXTERNAL_PROVIDER_INVALID_RESPONSE: 'Invalid response from external provider',
}

export const API_ERRORS_LABELS = {
  GAME_IS_STARTED_AND_USER_NOT_JOINED: 'GAME_IS_STARTED_AND_USER_NOT_JOINED',
  EMAIL_VERIFICATION: 'USER_IS_NOT_VERIFIED',
  USER_HAS_NOT_JOINED_PARENT_GAME: 'USER_HAS_NOT_JOINED_PARENT_GAME',
  USER_WRONG_PASSWORD: 'USER_WRONG_PASSWORD',
  USER_ALREADY_JOINED: 'USER_ALREADY_JOINED',
  PASSWORD_MIGRATION_REQUIRED: 'PASSWORD_MIGRATION_REQUIRED',
  EXTERNAL_PROVIDER_USER_NOT_FOUND: 'EXTERNAL_PROVIDER_USER_NOT_FOUND',
}

export const PROFILE_SERVICE_ERRORS = {
  USER_EMAIL_ALREADY_EXISTS: 'User with this email already exists',
  USER_WRONG_PASSWORD: 'Wrong password',
  USER_DOES_NOT_EXIST: `We couldn’t find an account with that email`,
}

export const STATUS_CODES_TO_ERRORS = {
  422: PROFILE_SERVICE_ERRORS.USER_EMAIL_ALREADY_EXISTS,
}

export const RAFFLE_API_ERRORS = {
  RAFFLE_MONTHLY_SPEND_LIMIT_EXCEEDED: 'RAFFLE_MONTHLY_SPEND_LIMIT_EXCEEDED',
  RAFFLE_NOT_ENOUGH_TICKETS: 'RAFFLE_NOT_ENOUGH_TICKETS',
  RAFFLE_NOT_ACTIVE: 'RAFFLE_NOT_ACTIVE',
  CHECKOUT_PAYMENT_NOT_REQUIRED: 'CHECKOUT_PAYMENT_NOT_REQUIRED',
}
